import React, { useState, useRef } from 'react';
import './FileUpload.css';

function FileUpload({ onFileUpload, onProceed, loading, priorYearData }) {
  const [spouseName, setSpouseName] = useState('');
  const [showSpouseUpload, setShowSpouseUpload] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(null); // tracks which file is uploading
  const [incomeDocTypes] = useState(['W2', '1099-NEC', '1099-MISC']);
  const [uploadMode, setUploadMode] = useState({ primary: 'separate', spouse: 'separate' }); // separate or combined per filer
  
  const fileInputRefs = useRef({});

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e, spouseNumber, returnType) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      if (files[0].type === 'application/pdf') {
        setUploadingFile(`${spouseNumber}-${returnType}`);
        onFileUpload(files[0], spouseNumber, spouseNumber === 1 ? 'Primary Filer' : spouseName, returnType, 'prior');
      } else {
        alert('Please upload a PDF file');
      }
    }
  };

  const handleDropIncome = (e, spouseNumber, docType) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      if (files[0].type === 'application/pdf') {
        setUploadingFile(`income-${spouseNumber}-${docType}`);
        onFileUpload(files[0], spouseNumber, spouseNumber === 1 ? 'Primary Filer' : spouseName, docType, 'income');
      } else {
        alert('Please upload a PDF file');
      }
    }
  };

  const handleDropCombined = (e, spouseNumber) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      if (files[0].type === 'application/pdf') {
        setUploadingFile(`combined-${spouseNumber}`);
        // Upload the same file as both federal and state
        handleCombinedUpload(files[0], spouseNumber).catch(err => {
          console.error('Failed to upload combined PDF:', err);
        });
      } else {
        alert('Please upload a PDF file');
      }
    }
  };

  const handleCombinedUpload = async (file, spouseNumber) => {
    const spName = spouseNumber === 1 ? 'Primary Filer' : spouseName;
    try {
      console.log('🔗 Combined Upload Started', { file: file.name, size: file.size, spouse: spouseNumber });
      
      // Upload as federal first
      console.log('📤 Uploading combined PDF as FEDERAL...');
      await onFileUpload(file, spouseNumber, spName, 'federal', 'prior');
      console.log('✅ Federal upload complete');
      
      // Then upload as state
      console.log('📤 Uploading combined PDF as STATE...');
      await onFileUpload(file, spouseNumber, spName, 'state', 'prior');
      console.log('✅ State upload complete');
      console.log('🎉 Combined upload finished successfully');
    } catch (err) {
      console.error('❌ Combined upload error:', err);
      throw err;
    }
  };

  const handleChangeCombined = (e, spouseNumber) => {
    const files = e.target.files;
    if (files && files[0]) {
      setUploadingFile(`combined-${spouseNumber}`);
      handleCombinedUpload(files[0], spouseNumber).catch(err => {
        console.error('Failed to upload combined PDF:', err);
      });
    }
  };

  const handleChange = (e, spouseNumber, returnType) => {
    const files = e.target.files;
    if (files && files[0]) {
      setUploadingFile(`${spouseNumber}-${returnType}`);
      onFileUpload(files[0], spouseNumber, spouseNumber === 1 ? 'Primary Filer' : spouseName, returnType, 'prior');
    }
  };

  const handleChangeIncome = (e, spouseNumber, docType) => {
    const files = e.target.files;
    if (files && files[0]) {
      setUploadingFile(`income-${spouseNumber}-${docType}`);
      onFileUpload(files[0], spouseNumber, spouseNumber === 1 ? 'Primary Filer' : spouseName, docType, 'income');
    }
  };

  const handleClick = (spouseNumber, returnType) => {
    const refKey = `${spouseNumber}-${returnType}`;
    fileInputRefs.current[refKey]?.click();
  };

  const handleClickIncome = (spouseNumber, docType) => {
    const refKey = `income-${spouseNumber}-${docType}`;
    fileInputRefs.current[refKey]?.click();
  };

  const isUploading = (spouseNumber, returnType) => uploadingFile === `${spouseNumber}-${returnType}`;
  const isUploadingIncome = (spouseNumber, docType) => uploadingFile === `income-${spouseNumber}-${docType}`;

  const hasFederalUpload = (spouseNumber) => {
    if (spouseNumber === 1) {
      return priorYearData?.primary?.federal ? true : false;
    } else {
      return priorYearData?.spouse?.federal ? true : false;
    }
  };

  const hasStateUpload = (spouseNumber) => {
    if (spouseNumber === 1) {
      return priorYearData?.primary?.state ? true : false;
    } else {
      return priorYearData?.spouse?.state ? true : false;
    }
  };

  const hasCombinedUpload = (spouseNumber) => {
    return hasFederalUpload(spouseNumber) && hasStateUpload(spouseNumber);
  };

  const hasIncomeDoc = (spouseNumber, docType) => {
    if (spouseNumber === 1) {
      return priorYearData?.primary?.income?.[docType] ? true : false;
    } else {
      return priorYearData?.spouse?.income?.[docType] ? true : false;
    }
  };

  const getIncomeAmount = (spouseNumber, docType) => {
    if (spouseNumber === 1) {
      return priorYearData?.primary?.income?.[docType]?.incomeAmount || 0;
    } else {
      return priorYearData?.spouse?.income?.[docType]?.incomeAmount || 0;
    }
  };

  const getFederalData = (spouseNumber) => {
    return spouseNumber === 1 ? priorYearData?.primary?.federal : priorYearData?.spouse?.federal;
  };

  const getStateData = (spouseNumber) => {
    return spouseNumber === 1 ? priorYearData?.primary?.state : priorYearData?.spouse?.state;
  };

  const renderReturnUpload = (spouseNumber, returnType, label) => {
    const isUploaded = returnType === 'federal' ? hasFederalUpload(spouseNumber) : hasStateUpload(spouseNumber);
    const return_data = returnType === 'federal' ? getFederalData(spouseNumber) : getStateData(spouseNumber);
    const refKey = `${spouseNumber}-${returnType}`;
    const isLoading = isUploading(spouseNumber, returnType);

    return (
      <div key={returnType} className="return-upload">
        <h5>{label}</h5>
        {!isUploaded ? (
          <div
            className={`upload-area ${isLoading ? 'loading' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={(e) => handleDrop(e, spouseNumber, returnType)}
          >
            <input
              ref={(el) => { fileInputRefs.current[refKey] = el; }}
              type="file"
              accept=".pdf"
              onChange={(e) => handleChange(e, spouseNumber, returnType)}
              disabled={isLoading}
              style={{ display: 'none' }}
            />

            {isLoading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Processing {label}...</p>
              </div>
            ) : (
              <>
                <svg
                  className="upload-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <p>Drag and drop {label.toLowerCase()} or</p>
                <button
                  className="upload-button small"
                  onClick={() => handleClick(spouseNumber, returnType)}
                  disabled={isLoading}
                >
                  Select File
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="upload-success compact">
            <div className="success-icon">✓</div>
            <p><strong>{label}</strong> Uploaded</p>
            {return_data && return_data.totalIncome > 0 && (
              <p className="small-text">${return_data.totalIncome.toLocaleString()} income</p>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderIncomeUpload = (spouseNumber, docType) => {
    const isUploaded = hasIncomeDoc(spouseNumber, docType);
    const incomeAmount = getIncomeAmount(spouseNumber, docType);
    const refKey = `income-${spouseNumber}-${docType}`;
    const isLoading = isUploadingIncome(spouseNumber, docType);

    return (
      <div key={docType} className="income-upload">
        <h5>{docType}</h5>
        {!isUploaded ? (
          <div
            className={`upload-area compact ${isLoading ? 'loading' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={(e) => handleDropIncome(e, spouseNumber, docType)}
          >
            <input
              ref={(el) => { fileInputRefs.current[refKey] = el; }}
              type="file"
              accept=".pdf"
              onChange={(e) => handleChangeIncome(e, spouseNumber, docType)}
              disabled={isLoading}
              style={{ display: 'none' }}
            />

            {isLoading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Processing {docType}...</p>
              </div>
            ) : (
              <>
                <svg
                  className="upload-icon small"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <p>Drag {docType} or</p>
                <button
                  className="upload-button small"
                  onClick={() => handleClickIncome(spouseNumber, docType)}
                  disabled={isLoading}
                >
                  Upload
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="upload-success compact">
            <div className="success-icon">✓</div>
            <p><strong>{docType}</strong> ✓</p>
            {incomeAmount > 0 && (
              <p className="small-text">${incomeAmount.toLocaleString()}</p>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderCombinedUpload = (spouseNumber) => {
    const isUploaded = hasCombinedUpload(spouseNumber);
    const refKey = `combined-${spouseNumber}`;
    const isLoading = uploadingFile === `combined-${spouseNumber}`;

    return (
      <div className="combined-upload-section">
        <h4>Single Combined PDF (Federal + State)</h4>
        {!isUploaded ? (
          <div
            className={`upload-area ${isLoading ? 'loading' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={(e) => handleDropCombined(e, spouseNumber)}
          >
            <input
              ref={(el) => { fileInputRefs.current[refKey] = el; }}
              type="file"
              accept=".pdf"
              onChange={(e) => handleChangeCombined(e, spouseNumber)}
              disabled={isLoading}
              style={{ display: 'none' }}
            />

            {isLoading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Processing combined PDF...</p>
              </div>
            ) : (
              <>
                <svg
                  className="upload-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <p>Drag combined PDF or</p>
                <button
                  className="upload-button small"
                  onClick={() => fileInputRefs.current[refKey]?.click()}
                  disabled={isLoading}
                >
                  Select File
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="upload-success compact">
            <div className="success-icon">✓</div>
            <p><strong>Combined PDF</strong> Uploaded</p>
            <p className="small-text">Federal + State</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="file-upload-container">
      <h2>Step 1: Upload Tax Returns (Federal & State)</h2>
      <p className="upload-description">
        Upload your prior year Federal and State tax returns. You can upload them as separate PDFs or as a single combined PDF.
      </p>

      <div className="uploads-wrapper">
        {/* Primary Filer Section */}
        <div className="filer-section">
          <h3>Primary Filer</h3>
          
          {/* Upload Mode Toggle */}
          {!hasCombinedUpload(1) && !hasFederalUpload(1) && !hasStateUpload(1) && (
            <div className="upload-mode-toggle">
              <label>Choose upload option:</label>
              <div className="toggle-buttons">
                <button
                  className={`toggle-btn ${uploadMode.primary === 'separate' ? 'active' : ''}`}
                  onClick={() => setUploadMode({ ...uploadMode, primary: 'separate' })}
                >
                  Separate PDFs (Federal + State)
                </button>
                <button
                  className={`toggle-btn ${uploadMode.primary === 'combined' ? 'active' : ''}`}
                  onClick={() => setUploadMode({ ...uploadMode, primary: 'combined' })}
                >
                  Single Combined PDF
                </button>
              </div>
            </div>
          )}

          {/* Separate Returns Upload */}
          {uploadMode.primary === 'separate' && !hasCombinedUpload(1) && (
            <div className="returns-grid">
              {renderReturnUpload(1, 'federal', 'Federal Return')}
              {renderReturnUpload(1, 'state', 'State Return')}
            </div>
          )}

          {/* Combined Return Upload */}
          {uploadMode.primary === 'combined' && !hasCombinedUpload(1) && (
            renderCombinedUpload(1)
          )}

          {/* Already Uploaded - Show What Was Uploaded */}
          {hasCombinedUpload(1) && (
            <div className="upload-success compact">
              <div className="success-icon">✓</div>
              <p><strong>Returns Uploaded</strong></p>
              <p className="small-text">Federal + State</p>
            </div>
          )}
        </div>

        {/* Spouse Upload Toggle */}
        {hasFederalUpload(1) && hasStateUpload(1) && (
          <div className="spouse-toggle">
            {!showSpouseUpload && !priorYearData?.spouse?.federal ? (
              <button
                className="add-spouse-button"
                onClick={() => setShowSpouseUpload(true)}
              >
                + Add Spouse's Returns
              </button>
            ) : null}
          </div>
        )}

        {/* Spouse Section */}
        {(showSpouseUpload || priorYearData?.spouse?.federal || priorYearData?.spouse?.state) && (
          <div className="filer-section spouse-section">
            <div className="section-header">
              <h3>Spouse (Optional)</h3>
              <p className="helper-text">
                Upload your spouse's Federal and State returns for accurate MFS analysis
              </p>
            </div>

            {!priorYearData?.spouse?.federal && showSpouseUpload && (
              <div className="spouse-name-input">
                <label htmlFor="spouseName">Spouse's Name (Optional):</label>
                <input
                  id="spouseName"
                  type="text"
                  value={spouseName}
                  onChange={(e) => setSpouseName(e.target.value)}
                  placeholder="e.g., Jane"
                  maxLength="50"
                />
              </div>
            )}

            {/* Upload Mode Toggle */}
            {!hasCombinedUpload(2) && !priorYearData?.spouse?.federal && !priorYearData?.spouse?.state && (
              <div className="upload-mode-toggle">
                <label>Choose upload option:</label>
                <div className="toggle-buttons">
                  <button
                    className={`toggle-btn ${uploadMode.spouse === 'separate' ? 'active' : ''}`}
                    onClick={() => setUploadMode({ ...uploadMode, spouse: 'separate' })}
                  >
                    Separate PDFs (Federal + State)
                  </button>
                  <button
                    className={`toggle-btn ${uploadMode.spouse === 'combined' ? 'active' : ''}`}
                    onClick={() => setUploadMode({ ...uploadMode, spouse: 'combined' })}
                  >
                    Single Combined PDF
                  </button>
                </div>
              </div>
            )}

            {/* Separate Returns Upload */}
            {uploadMode.spouse === 'separate' && !hasCombinedUpload(2) && (
              <div className="returns-grid">
                {renderReturnUpload(2, 'federal', 'Federal Return')}
                {renderReturnUpload(2, 'state', 'State Return')}
              </div>
            )}

            {/* Combined Return Upload */}
            {uploadMode.spouse === 'combined' && !hasCombinedUpload(2) && (
              renderCombinedUpload(2)
            )}

            {/* Already Uploaded - Show What Was Uploaded */}
            {hasCombinedUpload(2) && (
              <div className="upload-success compact">
                <div className="success-icon">✓</div>
                <p><strong>Returns Uploaded</strong></p>
                <p className="small-text">Federal + State</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Current Year Income Documents Section */}
      <div className="uploads-wrapper">
        {/* Primary Filer Income */}
        <div className="filer-section income-section">
          <div className="section-header">
            <h3>Current Year Income Documents (Optional)</h3>
            <p className="helper-text">
              Upload W2, 1099-NEC, or 1099-MISC to estimate current year tax liability
            </p>
          </div>
          <div className="income-grid">
            {incomeDocTypes.map(docType => renderIncomeUpload(1, docType))}
          </div>
        </div>

        {/* Spouse Income Documents */}
        {(priorYearData?.spouse?.federal || priorYearData?.spouse?.state) && (
          <div className="filer-section income-section spouse-section">
            <div className="section-header">
              <h3>Spouse's Current Year Income (Optional)</h3>
              <p className="helper-text">
                Upload spouse's W2, 1099-NEC, or 1099-MISC documents
              </p>
            </div>
            <div className="income-grid">
              {incomeDocTypes.map(docType => renderIncomeUpload(2, docType))}
            </div>
          </div>
        )}
      </div>

      {/* Progress Indicator */}
      {(hasFederalUpload(1) || hasStateUpload(1)) && (
        <div className="proceed-info">
          <div className="progress-checklist">
            <div className="checklist-item">
              <span className={hasFederalUpload(1) ? 'checked' : 'unchecked'}>
                {hasFederalUpload(1) ? '✓' : '○'}
              </span>
              <span>Primary Filer - Federal</span>
            </div>
            <div className="checklist-item">
              <span className={hasStateUpload(1) ? 'checked' : 'unchecked'}>
                {hasStateUpload(1) ? '✓' : '○'}
              </span>
              <span>Primary Filer - State</span>
            </div>
            {(priorYearData?.spouse?.federal || priorYearData?.spouse?.state) && (
              <>
                <div className="checklist-item">
                  <span className={hasFederalUpload(2) ? 'checked' : 'unchecked'}>
                    {hasFederalUpload(2) ? '✓' : '○'}
                  </span>
                  <span>Spouse - Federal</span>
                </div>
                <div className="checklist-item">
                  <span className={hasStateUpload(2) ? 'checked' : 'unchecked'}>
                    {hasStateUpload(2) ? '✓' : '○'}
                  </span>
                  <span>Spouse - State</span>
                </div>
              </>
            )}
          </div>
          {hasFederalUpload(1) && hasStateUpload(1) && (
            <p className="ready-indicator">✓ Ready to proceed</p>
          )}
        </div>
      )}

      {/* Proceed Button */}
      {hasFederalUpload(1) && hasStateUpload(1) && (
        <div className="proceed-button-section">
          <p className="proceed-prompt">
            You've uploaded your prior year returns. You can now add current year income documents (1099/W2) or proceed to questions.
          </p>
          <button
            className="button primary proceed-button"
            onClick={onProceed}
            disabled={loading}
          >
            Proceed to Questions →
          </button>
        </div>
      )}
    </div>
  );
}

export default FileUpload;
